/**
 * ReflectionPage - Question flow with response saving
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/shared/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReflectionPage = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [existingResponses, setExistingResponses] = useState({});

  const userId = sessionStorage.getItem('user_id');
  const relationshipId = sessionStorage.getItem('relationship_id');
  const level = sessionStorage.getItem('selected_level') || 'CORE';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch questions and existing responses in parallel
      const [questionsRes, responsesRes] = await Promise.all([
        axios.get(`${API}/questions/${level}`),
        axios.get(`${API}/responses/${relationshipId}/${userId}`)
      ]);

      setQuestions(questionsRes.data);
      
      // Build existing responses map
      const existingMap = {};
      responsesRes.data.forEach(r => {
        existingMap[r.question_id] = r.response_json?.value;
      });
      setExistingResponses(existingMap);
      setResponses(existingMap); // Pre-populate responses
      
      // Check if resuming from review page
      const resumeIndex = sessionStorage.getItem('resume_question_index');
      if (resumeIndex !== null) {
        setCurrentIndex(parseInt(resumeIndex, 10));
        sessionStorage.removeItem('resume_question_index');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load questions');
      setLoading(false);
    }
  };

  const handleResponseChange = (value) => {
    const currentQuestion = questions[currentIndex];
    setResponses({
      ...responses,
      [currentQuestion.question_id]: value
    });
  };

  const saveCurrentResponse = async () => {
    const currentQuestion = questions[currentIndex];
    const currentValue = responses[currentQuestion.question_id];
    
    // Only save if there's a value
    if (currentValue === undefined || currentValue === null || currentValue === '') {
      return true; // Allow navigation even without response
    }

    try {
      await axios.post(`${API}/responses`, {
        relationship_id: relationshipId,
        question_id: currentQuestion.question_id,
        response_data: {
          user_id: userId,
          value: currentValue
        }
      });
      return true;
    } catch (error) {
      console.error('Error saving response:', error);
      toast.error('Failed to save response');
      return false;
    }
  };

  const handleNext = async () => {
    const saved = await saveCurrentResponse();
    if (!saved) return;

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Navigate to Review page instead of Lock page
      navigate('/review');
    }
  };

  const handlePrevious = async () => {
    if (currentIndex > 0) {
      // Save current response before going back
      await saveCurrentResponse();
      setCurrentIndex(currentIndex - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center" style={{minHeight: 'calc(100vh - 100px)'}}>
          <Loader2 className="w-8 h-8 animate-spin text-stone-900" />
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const hasResponse = responses[currentQuestion.question_id] !== undefined && 
                      responses[currentQuestion.question_id] !== null && 
                      responses[currentQuestion.question_id] !== '';

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Progress Bar */}
      <div className="fixed top-[100px] left-0 w-full h-1 bg-stone-100 z-10">
        <div 
          className="h-full bg-mc-blue-deep transition-all duration-300"
          style={{width: `${progress}%`}}
        />
      </div>

      <div className="py-20 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <span className="text-sm text-stone-500">
              Question {currentIndex + 1} of {questions.length}
            </span>
            <button
              onClick={() => navigate('/review')}
              className="text-sm text-purple-600 hover:text-purple-800 font-medium"
            >
              Review All Answers →
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-8 sm:p-12">
            <p className="text-sm uppercase tracking-widest text-stone-500 mb-4">
              {currentQuestion.category}
            </p>
            
            <h3 className="text-xl sm:text-2xl font-semibold text-stone-900 mb-8">
              {currentQuestion.prompt_text}
            </h3>

            {/* Response Input */}
            {currentQuestion.response_type === 'long_text' && (
              <textarea
                value={responses[currentQuestion.question_id] || ''}
                onChange={(e) => handleResponseChange(e.target.value)}
                className="w-full h-48 rounded-lg border-stone-200 bg-stone-50 p-4 text-lg focus:border-stone-900 focus:ring-0 transition-all resize-none"
                placeholder="Type your response here..."
                data-testid="response-textarea"
              />
            )}

            {currentQuestion.response_type === 'multiple_choice' && (
              <div className="space-y-3">
                {currentQuestion.options_json?.choices?.map((choice, idx) => (
                  <label key={idx} className="flex items-center gap-3 p-4 rounded-lg border border-stone-200 hover:bg-stone-50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="choice"
                      value={choice}
                      checked={responses[currentQuestion.question_id] === choice}
                      onChange={(e) => handleResponseChange(e.target.value)}
                      className="w-5 h-5"
                    />
                    <span className="text-lg text-stone-700">{choice}</span>
                  </label>
                ))}
              </div>
            )}

            {currentQuestion.response_type === 'multi_select' && (
              <div className="space-y-3">
                {currentQuestion.options_json?.choices?.map((choice, idx) => (
                  <label key={idx} className="flex items-center gap-3 p-4 rounded-lg border border-stone-200 hover:bg-stone-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      value={choice}
                      checked={(responses[currentQuestion.question_id] || []).includes(choice)}
                      onChange={(e) => {
                        const current = responses[currentQuestion.question_id] || [];
                        if (e.target.checked) {
                          handleResponseChange([...current, choice]);
                        } else {
                          handleResponseChange(current.filter(c => c !== choice));
                        }
                      }}
                      className="w-5 h-5"
                    />
                    <span className="text-lg text-stone-700">{choice}</span>
                  </label>
                ))}
              </div>
            )}

            {currentQuestion.response_type === 'yes_no' && (
              <div className="space-y-3">
                {['Yes', 'No'].map((choice, idx) => (
                  <label key={idx} className="flex items-center gap-3 p-4 rounded-lg border border-stone-200 hover:bg-stone-50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="yesno"
                      value={choice}
                      checked={responses[currentQuestion.question_id] === choice}
                      onChange={(e) => handleResponseChange(e.target.value)}
                      className="w-5 h-5"
                    />
                    <span className="text-lg text-stone-700">{choice}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-12">
              <button
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                className="px-6 py-3 text-stone-600 hover:text-stone-900 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="previous-question"
              >
                <ArrowLeft className="w-5 h-5" />
                Previous
              </button>

              <button
                onClick={handleNext}
                className="bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-8 py-3 text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2"
                data-testid="next-question"
              >
                {currentIndex === questions.length - 1 ? 'Review Answers' : 'Next'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReflectionPage;
